import {
    AppState,
    AuthenticationActions,
    authPaywallBypass,
    debugExtendedAccess,
    isGoogleExtendedAccess,
    RequiredAccess,
    usePrevious,
} from '@news-mono/web-common'
import { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Logger } from 'typescript-log'
import { AuthenticationProviderContext } from '../user-registration/authentication/AuthenticationProvider'
import { entitledToViewPublication } from '../user-registration/entitlements/EntitledToView'
import {
    initGoogleExtendedAccess,
    showGoogleRegwall,
} from './inject-extended-access'

export interface GoogleExtendedAccessProps {
    requiredAccess: RequiredAccess
    articleSlug: string
    logger: Logger
}

let subscriptionsPromise: any

export function GoogleExtendedAccess({
    requiredAccess,
    articleSlug,
    logger,
}: GoogleExtendedAccessProps) {
    debugExtendedAccess('<GoogleExtendedAccess/>')
    const dispatch = useDispatch()

    const authentication = useSelector(
        (state: AppState) => state.authentication,
    )
    const { hasAttemptedValidation, isLoggedIn, googleLogin } = authentication

    const [hasCheckedMetering, setHasCheckedMetering] = useState(false)
    const previousArticleSlug = usePrevious(articleSlug)
    useEffect(() => {
        if (previousArticleSlug !== articleSlug) {
            setHasCheckedMetering(false)
        }
    }, [previousArticleSlug, articleSlug])

    const [googleInitialised, setGoogleInitialised] = useState(false)
    const authContext = useContext(AuthenticationProviderContext)
    useEffect(() => {
        if (authContext && !googleInitialised) {
            setGoogleInitialised(true)
            subscriptionsPromise = initGoogleExtendedAccess(
                authContext.getSubscribeLink({
                    callToAction: 'extended-access',
                }),
            )
        }
    }, [authContext, googleInitialised])

    const isEntitledToView = entitledToViewPublication(
        requiredAccess,
        authentication,
        articleSlug,
    )

    useEffect(() => {
        async function doMetering() {
            try {
                if (!isGoogleExtendedAccess()) {
                    debugExtendedAccess('Abort metering: not extended access')
                    return
                }
                if (!subscriptionsPromise) {
                    debugExtendedAccess('Abort metering: not initialised')
                }
                if (
                    authentication.googleLogin &&
                    authentication.googleLogin !== 'complete'
                ) {
                    debugExtendedAccess('Abort metering: login in process')
                    return
                }
                if (hasCheckedMetering) {
                    debugExtendedAccess('Abort metering: completed')
                    return
                }
                if (!hasAttemptedValidation) {
                    debugExtendedAccess(
                        'Abort metering: not attempted validation',
                    )
                    return
                }

                if (!isEntitledToView && !googleLogin) {
                    debugExtendedAccess(
                        'Initiating extended access intervention for google login',
                    )
                    dispatch({
                        type: AuthenticationActions.GOOGLE_LOGIN,
                        payload: { googleLogin: 'intervention' },
                    })
                    return showGoogleRegwall(
                        dispatch,
                        authentication,
                        logger,
                        subscriptionsPromise,
                    )
                }

                const { entitlementsPromise, subscriptions } =
                    await subscriptionsPromise

                if (requiredAccess.level !== 'subscriber') {
                    // Raise analytics event to tell google the user viewed a free article
                    debugExtendedAccess('Emitting event: unlocked free')
                    setHasCheckedMetering(true)
                    return subscriptions.setShowcaseEntitlement({
                        entitlement: 'EVENT_SHOWCASE_UNLOCKED_FREE_PAGE',
                        isUserRegistered: true,
                    })
                }

                if (requiredAccess.level === 'subscriber' && isEntitledToView) {
                    // Raise analytics event to tell google the user has entitlements to view a premium article
                    debugExtendedAccess('Emitting event: unlocked subscription')
                    setHasCheckedMetering(true)
                    return subscriptions.setShowcaseEntitlement({
                        entitlement: 'EVENT_SHOWCASE_UNLOCKED_BY_SUBSCRIPTION',
                        isUserRegistered: true,
                    })
                }

                const entitlements = await entitlementsPromise

                if (isLoggedIn && !isEntitledToView) {
                    debugExtendedAccess('Doing extended access metering')
                    if (entitlements.enablesThisWithGoogleMetering()) {
                        debugExtendedAccess(
                            'Unlocking article with extended access metering',
                        )
                        setHasCheckedMetering(true)
                        entitlements.consume(() => {
                            dispatch(
                                authPaywallBypass({
                                    bypassSlug: articleSlug,
                                    reason: 'Google extended access metering',
                                }),
                            )
                        })
                    } else {
                        debugExtendedAccess('Metering denied')
                        subscriptions.setShowcaseEntitlement({
                            entitlement:
                                'EVENT_SHOWCASE_NO_ENTITLEMENTS_PAYWALL',
                            isUserRegistered: true,
                        })
                    }
                    return
                }
                debugExtendedAccess('Ooops!')
            } catch (err) {
                logger.error({ err }, 'Extended access unhandled error')
            }
        }

        // Kick off async function to check metering
        doMetering()
    }, [
        articleSlug,
        authentication,
        dispatch,
        googleLogin,
        hasAttemptedValidation,
        hasCheckedMetering,
        isEntitledToView,
        isLoggedIn,
        requiredAccess.level,
        logger,
    ])

    return null
}
